import React, { FC, useCallback } from 'react';
import styled from 'styled-components';
import { DeniedPermissionHelper } from 'common/permissionHelpers/DeniedPermissionHelper';
import { SideNavBar } from '@utmost.co/utmost-design-system/src/components/SideNavBar/SideNavBar';
import { MobileSideNavBar } from '@utmost.co/utmost-design-system/src/components/SideNavBar/MobileSideNavBar';
import { MixpanelEvents } from 'src/integrations/mixpanel/Events/MixpanelEvents';
import { MainMenuExpandDetails } from 'src/integrations/mixpanel/Events/general';
import { useMixpanel } from 'src/integrations/mixpanel/useMixpanel';
import { MenuItem } from './MenuItem';
import { useMenuItems } from './useMenuItems';
import { MENU_SIDE_BAR_KEY } from 'common/menu/MENU_SIDE_BAR_KEY';
import { AuthUser } from 'src/providers/User/AuthUserModels';
import { DesktopLogos } from '../header/DesktopLogos';

interface Props {
  user: AuthUser;
  isMobileVersion?: true;
  showMobileLogo?: boolean;
}

export const Menu: FC<React.PropsWithChildren<Props>> = ({ user, isMobileVersion, showMobileLogo }) => {
  const { menuItems, checkPermission } = useMenuItems(user);
  const { track } = useMixpanel();

  const handleExpand = useCallback(
    (isExpanded: boolean) => {
      track<MainMenuExpandDetails>(MixpanelEvents.general.MAIN_MENU_EXPAND, { isExpanded });
    },
    [track]
  );

  return (
    <StyledWrapper>
      {isMobileVersion ? (
        <MobileSideNavBar>
          {showMobileLogo && <StyledDesktopLogos />}
          {menuItems.map(({ id, icon, text, isLastFromGroup, style }) => (
            <MenuItem key={id} id={id} icon={icon} text={text} isLastFromGroup={isLastFromGroup} style={style} />
          ))}
        </MobileSideNavBar>
      ) : (
        <SideNavBar maxWidth="150px" localStorageKey={MENU_SIDE_BAR_KEY} onExpand={handleExpand}>
          {menuItems.map(({ id, icon, text, isLastFromGroup, permission, subPermission, style }) => (
            <DeniedPermissionHelper
              key={id}
              permissions={permission?.name ? [{ name: permission.name, action: 'READ' }] : []}
              canRead={
                !permission || checkPermission(permission) || (subPermission ? checkPermission(subPermission) : false)
              }
            >
              <MenuItem id={id} icon={icon} text={text} isLastFromGroup={isLastFromGroup} style={style} />
            </DeniedPermissionHelper>
          ))}
        </SideNavBar>
      )}
    </StyledWrapper>
  );
};

const StyledWrapper = styled.div`
  height: 100%;
`;

const StyledDesktopLogos = styled(DesktopLogos)`
  padding-bottom: ${({ theme }) => theme.spacingSize.m};
  margin: ${({ theme }) => theme.spacingSize.m};
`;
